import { useCallback, useMemo } from 'react';
import { ApolloError, useLazyQuery } from '@apollo/client';
import { searchCoverageHours } from 'graphql-queries';

export const COVERAGE_HOURS_PAGE_SIZE = 25;

// Mirrors Target Times' LIST_PAGINATION - large enough to fetch every entry
// for a channel in one request.
const ALL_ENTRIES_PAGE_SIZE = 500;

export class CoverageHoursQueryError extends Error {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message, options);
    this.name = 'CoverageHoursQueryError';
  }
}

export type CoverageHoursDayOfWeek =
  | 'DAY_OF_WEEK_SUNDAY'
  | 'DAY_OF_WEEK_MONDAY'
  | 'DAY_OF_WEEK_TUESDAY'
  | 'DAY_OF_WEEK_WEDNESDAY'
  | 'DAY_OF_WEEK_THURSDAY'
  | 'DAY_OF_WEEK_FRIDAY'
  | 'DAY_OF_WEEK_SATURDAY';

export type CoverageHoursEntry = {
  id: string;
  routingId: string;
  category: string;
  issue: string | null;
  teamId: string | null;
  teamName: string | null;
  daysOfWeek: CoverageHoursDayOfWeek[];
  startMinute: number;
  endMinute: number;
  timezone: string | null;
};

export type CoverageHoursPairing = Pick<
  CoverageHoursEntry,
  'category' | 'issue' | 'routingId'
>;

export type CoverageHoursPageInfo = {
  start: number;
  total: number;
  perPage: number;
};

type WireEntry = CoverageHoursEntry & { __typename?: string };

type WirePageInfo = CoverageHoursPageInfo & { __typename?: string };

type SearchCoverageHoursResponse = {
  searchCoverageHours: {
    coverageHours: WireEntry[];
    pageInfo: WirePageInfo | null;
  };
};

export type UseCoverageHoursArgs = {
  channelId: string;
  pageIndex: number;
  perPage?: number;
};

export type UseCoverageHoursResult = {
  entries: CoverageHoursEntry[];
  pageInfo: CoverageHoursPageInfo | null;
  totalRows: number;
  loading: boolean;
  error?: CoverageHoursQueryError;
  fetchCoverageHours: () => Promise<CoverageHoursEntry[]>;
  refreshCoverageHours: () => Promise<void>;
};

const NO_ENTRIES: CoverageHoursEntry[] = [];

function toCoverageHoursEntry(entry: WireEntry): CoverageHoursEntry {
  return {
    id: entry.id,
    routingId: entry.routingId,
    category: entry.category,
    issue: entry.issue ?? null,
    teamId: entry.teamId ?? null,
    teamName: entry.teamName ?? null,
    daysOfWeek: entry.daysOfWeek,
    startMinute: entry.startMinute,
    endMinute: entry.endMinute,
    timezone: entry.timezone ?? null,
  };
}

function toCoverageHoursPageInfo(
  pageInfo: WirePageInfo | null | undefined
): CoverageHoursPageInfo | null {
  if (!pageInfo) {
    return null;
  }

  return {
    start: pageInfo.start,
    total: pageInfo.total,
    perPage: pageInfo.perPage,
  };
}

export function useCoverageHours({
  channelId,
  pageIndex,
  perPage = COVERAGE_HOURS_PAGE_SIZE,
}: UseCoverageHoursArgs): UseCoverageHoursResult {
  const variables = useMemo(
    () => ({
      channelId,
      pagination: {
        start: pageIndex * perPage,
        perPage,
      },
    }),
    [channelId, pageIndex, perPage]
  );

  const [
    executeSearch,
    { data, previousData, loading, called, error: queryError },
  ] = useLazyQuery<SearchCoverageHoursResponse>(searchCoverageHours, {
    fetchPolicy: 'network-only',
  });

  const fetchCoverageHours = useCallback(async () => {
    const result = await executeSearch({ variables });

    return result.data
      ? result.data.searchCoverageHours.coverageHours.map(toCoverageHoursEntry)
      : [];
  }, [executeSearch, variables]);

  const refreshCoverageHours = useCallback(async () => {
    let refreshError: ApolloError | undefined;

    try {
      refreshError = (await executeSearch({ variables })).error;
    } catch (cause) {
      throw new CoverageHoursQueryError('Could not load coverage hours', {
        cause,
      });
    }

    if (refreshError) {
      throw new CoverageHoursQueryError('Could not load coverage hours', {
        cause: refreshError,
      });
    }
  }, [executeSearch, variables]);

  // previousData keeps the rows and the pagination bar mounted while the next
  // page is in flight.
  const result =
    data?.searchCoverageHours ?? previousData?.searchCoverageHours ?? null;

  const entries = useMemo(
    () =>
      result ? result.coverageHours.map(toCoverageHoursEntry) : NO_ENTRIES,
    [result]
  );

  const pageInfo = useMemo(
    () => toCoverageHoursPageInfo(result?.pageInfo),
    [result]
  );

  const error = useMemo(() => {
    if (!queryError) {
      return undefined;
    }

    return new CoverageHoursQueryError('Could not load coverage hours', {
      cause: queryError,
    });
  }, [queryError]);

  return {
    entries,
    pageInfo,
    totalRows: pageInfo?.total ?? 0,
    loading: loading || !called,
    error,
    fetchCoverageHours,
    refreshCoverageHours,
  };
}

export function useAllCoverageHours(channelId: string): UseCoverageHoursResult {
  return useCoverageHours({
    channelId,
    pageIndex: 0,
    perPage: ALL_ENTRIES_PAGE_SIZE,
  });
}
